/**
 * useLocalStorageFlag — try/catch-wrapped persistent state backed by
 * localStorage. Safe in private-mode / Safari-ITP / SSR — falls back
 * to pure React state silently if storage access throws.
 *
 * Use for anything where you want "did the user see this / dismiss
 * this / opt into this, ever, on this device?" — onboarding tours,
 * dismissed banners, remembered UI preferences, etc.
 *
 * Semantics:
 *   - On first render, reads `key` once. If present and parseable
 *     as JSON, that becomes the initial value. Otherwise
 *     `defaultValue` is used.
 *   - `setValue(newVal)` updates both React state AND localStorage.
 *     Storage failures are swallowed — the in-memory state always
 *     succeeds so your UI still works.
 *   - Values are JSON-serialised, so objects/arrays/booleans all
 *     round-trip correctly. Don't pass functions or undefined.
 *   - Intentionally does NOT sync across tabs — the `storage` event
 *     is rarely what you want for onboarding flags, and the cost
 *     of subscribing adds up at scale.
 *
 * Example:
 *   const [seenTour, setSeenTour] = useLocalStorageFlag(
 *     'demo_toolkit_tour_seen_v1',
 *     false,
 *   );
 *   // later:
 *   setSeenTour(true);
 */
import { useCallback, useState } from 'react';


function _safeRead(key, defaultValue) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}


export function useLocalStorageFlag(key, defaultValue) {
  // Lazy initializer — avoids calling into localStorage on every
  // render and lets the hook work in SSR / test environments where
  // `window` may not exist at module-eval time.
  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined') return defaultValue;
    return _safeRead(key, defaultValue);
  });

  const update = useCallback(
    (next) => {
      // Support functional updates: `setValue(v => !v)`.
      const resolved = typeof next === 'function' ? next(value) : next;
      setValue(resolved);
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved));
      } catch {
        /* private-mode / quota exceeded — silently keep in-memory state */
      }
    },
    [key, value],
  );

  const remove = useCallback(() => {
    setValue(defaultValue);
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  }, [key, defaultValue]);

  return [value, update, remove];
}
